import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

const BLANK = {
  company_name:   '',
  contact_person: '',
  contact_number: '',
  business_id:    '',
  email:          '',
  gst_number:     '',
  notes:          '',
  panel_access:   false,
  username:       '',
  password:       '',
};

export default function Dealers() {
  const navigate   = useNavigate();
  const [dealers,    setDealers]    = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [stFilter, setStFilter] = useState('');

  const [modal,   setModal]   = useState(null);   // 'add'|'edit'|'delete'
  const [selDlr,  setSelDlr]  = useState(null);
  const [form,    setForm]    = useState(BLANK);
  const [saving,  setSaving]  = useState(false);
  const [errs,    setErrs]    = useState({});

  const [toast, setToast] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/dealers');
      setDealers(r.data);
      return r.data;
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    api.get('/api/businesses').then(r => setBusinesses(r.data)).catch(() => {});
  }, []);

  const openAdd = () => { setForm(BLANK); setErrs({}); setSelDlr(null); setModal('add'); };
  const openEdit = (d) => {
    setForm({
      company_name:   d.company_name   || '',
      contact_person: d.contact_person || '',
      contact_number: d.contact_number || '',
      business_id:    d.business_id    || '',
      email:          d.email          || '',
      gst_number:     d.gst_number     || '',
      notes:          d.notes          || '',
      panel_access:   d.panel_access   || false,
      username:       d.username       || '',
      password:       d.plain_password || '',
    });
    setErrs({}); setSelDlr(d); setModal('edit');
  };
  const openDelete = (d) => { setSelDlr(d); setModal('delete'); };
  const closeModal = () => { setModal(null); setSelDlr(null); setErrs({}); };

  const f = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrs(p => ({ ...p, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim())   e.company_name   = 'Company name is required.';
    if (!form.contact_person.trim()) e.contact_person = 'Contact person is required.';
    if (!form.contact_number.trim()) e.contact_number = 'Contact number is required.';
    if (form.panel_access) {
      if (!form.username.trim()) e.username = 'Username is required for panel access.';
      if (!selDlr && !form.password.trim()) e.password = 'Password is required.';
    }
    return e;
  };

  const save = async () => {
    const e = validate();
    if (Object.keys(e).length) return setErrs(e);
    setSaving(true);
    try {
      const body = {
        company_name:   form.company_name.trim(),
        contact_person: form.contact_person.trim(),
        contact_number: form.contact_number.trim(),
        business_id:    form.business_id ? Number(form.business_id) : null,
        email:          form.email.trim()      || null,
        gst_number:     form.gst_number.trim() || null,
        notes:          form.notes.trim()      || null,
        panel_access:   form.panel_access,
        username:       form.panel_access ? (form.username.trim() || null) : null,
        password:       form.panel_access && form.password.trim() && form.password.trim() !== (selDlr?.plain_password || '')
                          ? form.password.trim() : null,
      };
      if (selDlr) await api.put(`/api/dealers/${selDlr.id}`, body);
      else        await api.post('/api/dealers', body);
      await loadAll();
      showToast('success', selDlr ? 'Dealer updated.' : 'Dealer added.');
      closeModal();
    } catch (ex) {
      showToast('error', ex?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/dealers/${selDlr.id}`);
      await loadAll();
      showToast('success', 'Dealer deleted.');
      closeModal();
    } catch (ex) {
      showToast('error', ex?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (d) => {
    const next = d.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/dealers/${d.id}`, { status: next });
      setDealers(prev => prev.map(x => x.id === d.id ? { ...x, status: next } : x));
    } catch { showToast('error', 'Status update failed.'); }
  };

  const filtered = dealers.filter(d => {
    const q = search.toLowerCase();
    return (
      (!q || d.company_name?.toLowerCase().includes(q)
          || d.dealer_id?.toLowerCase().includes(q)
          || d.contact_person?.toLowerCase().includes(q)
          || d.contact_number?.includes(q)
          || d.email?.toLowerCase().includes(q))
      && (!stFilter || d.status === stFilter)
    );
  });

  return (
    <Layout title="Dealers">

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Summary */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Total Dealers', val: dealers.length,                                      dot: C.primary },
          { label: 'Active',        val: dealers.filter(d => d.status === 'active').length,   dot: C.success },
          { label: 'Inactive',      val: dealers.filter(d => d.status === 'inactive').length, dot: C.textSub },
        ].map(({ label, val, dot }) => (
          <div key={label} style={{
            background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px',
            padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
            <div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        <button onClick={openAdd} style={{ ...btnPrimary, marginLeft: 'auto' }}>
          + Add Dealer
        </button>
      </div>

      {/* Search / filter bar */}
      <div style={{ ...card, marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search company, contact, email…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selStyle}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {(search || stFilter) && (
            <button onClick={() => { setSearch(''); setStFilter(''); }} style={btnGhost}>Clear</button>
          )}
          <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {dealers.length}</span>
        </div>
      </div>

      {/* Card grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.textSub }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{
          background: C.white, borderRadius: '14px',
          border: `2px dashed ${C.border}`, padding: '60px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '14px' }}>🤝</div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>
            {dealers.length === 0 ? 'No Dealers Added Yet' : 'No Results Found'}
          </div>
          <div style={{ fontSize: '13px', color: C.textSub, marginBottom: '20px' }}>
            {dealers.length === 0 ? 'Click "+ Add Dealer" to get started.' : 'Try adjusting your search or filters.'}
          </div>
          {dealers.length === 0 && <button onClick={openAdd} style={btnPrimary}>+ Add Dealer</button>}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {filtered.map(d => (
            <DealerCard
              key={d.id} d={d}
              onView={() => navigate(`/dealers/${d.id}`)}
              onEdit={() => openEdit(d)}
              onDelete={() => openDelete(d)}
              onToggleStatus={() => toggleStatus(d)}
            />
          ))}
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {(modal === 'add' || modal === 'edit') && (
        <Overlay>
          <div style={modalBox}>
            <MHeader
              title={modal === 'add' ? 'Add Dealer' : `${selDlr?.company_name} — ${selDlr?.dealer_id}`}
              onClose={closeModal}
            />
            <div style={{ padding: '24px 28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <FF label="Company Name *" error={errs.company_name}>
                  <FInput value={form.company_name} onChange={v => f('company_name', v)}
                    placeholder="Enter Company Name" err={!!errs.company_name} />
                </FF>
                <FF label="Contact Person *" error={errs.contact_person}>
                  <FInput value={form.contact_person} onChange={v => f('contact_person', v)}
                    placeholder="Enter Contact Person" err={!!errs.contact_person} />
                </FF>
                <FF label="Contact Number *" error={errs.contact_number}>
                  <FInput value={form.contact_number} onChange={v => f('contact_number', v)}
                    placeholder="Enter Contact Number" err={!!errs.contact_number} />
                </FF>
                <FF label="Works for Business">
                  <FSelect value={form.business_id} onChange={v => f('business_id', v)}>
                    <option value="">— Select business —</option>
                    {businesses.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.name}{b.is_default ? ' (default)' : ''}
                      </option>
                    ))}
                  </FSelect>
                </FF>
                <FF label="Email Address">
                  <FInput type="email" value={form.email} onChange={v => f('email', v)}
                    placeholder="Enter Email Address" />
                </FF>
                <FF label="GST Number">
                  <FInput value={form.gst_number} onChange={v => f('gst_number', v)}
                    placeholder="Enter GST Number" />
                </FF>
              </div>
              <FF label="Notes">
                <textarea
                  value={form.notes}
                  onChange={e => f('notes', e.target.value)}
                  placeholder="Any notes about this dealer…"
                  rows={3}
                  style={{
                    width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`,
                    borderRadius: '8px', fontSize: '13px', color: C.textMain,
                    outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                  }}
                />
              </FF>

              {/* Panel Access */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                border: `1.5px solid ${form.panel_access ? '#7C3AED' : C.border}`,
                background: form.panel_access ? '#F5F3FF' : C.white,
                userSelect: 'none', marginTop: '4px',
              }}>
                <input
                  type="checkbox"
                  checked={form.panel_access}
                  onChange={e => f('panel_access', e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: '#7C3AED', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>Panel Access</div>
                  <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>
                    Allow this dealer to log in to the panel with a username and password.
                  </div>
                </div>
              </label>

              {form.panel_access && (
                <div style={{
                  marginTop: '12px', padding: '16px', borderRadius: '10px',
                  background: '#F5F3FF', border: '1.5px solid #DDD6FE',
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px',
                }}>
                  <FF label="Username *" error={errs.username}>
                    <FInput
                      value={form.username}
                      onChange={v => f('username', v)}
                      placeholder="Enter username"
                      err={!!errs.username}
                    />
                  </FF>
                  <FF label={selDlr ? 'Password (edit to change)' : 'Password *'} error={errs.password}>
                    <FInput
                      type="text"
                      value={form.password}
                      onChange={v => f('password', v)}
                      placeholder={selDlr ? 'Leave blank to keep current' : 'Enter password'}
                      err={!!errs.password}
                    />
                  </FF>
                </div>
              )}
            </div>
            <MFooter onClose={closeModal} onSave={save} saving={saving}
              label={modal === 'add' ? '+ Add Dealer' : '💾 Update Dealer'} />
          </div>
        </Overlay>
      )}

      {/* ── Delete Modal ── */}
      {modal === 'delete' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <MHeader title="Delete Dealer" onClose={closeModal} />
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: '13px', color: C.textMain, lineHeight: 1.6, margin: 0 }}>
                Delete <strong style={{ color: C.danger }}>{selDlr?.company_name}</strong>{' '}
                <span style={{ fontSize: '11px', color: C.textSub }}>({selDlr?.dealer_id})</span>?
                <br /><span style={{ fontSize: '12px', color: C.textSub }}>This cannot be undone.</span>
              </p>
            </div>
            <MFooter onClose={closeModal} onSave={confirmDelete} saving={saving} label="Delete" danger />
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function DealerCard({ d, onView, onEdit, onDelete, onToggleStatus }) {
  const isActive = d.status === 'active';
  return (
    <div
      onClick={onView}
      style={{
        background: C.white, borderRadius: '14px',
        border: `1.5px solid ${isActive ? '#7C3AED' : C.border}`,
        boxShadow: isActive ? '0 0 0 3px #EDE9FE, 0 1px 4px rgba(0,0,0,0.06)' : '0 1px 4px rgba(0,0,0,0.05)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        cursor: 'pointer',
      }}
    >
      {/* Header */}
      <div style={{
        background: isActive
          ? 'linear-gradient(135deg, #3B0764 0%, #1E1040 100%)'
          : '#F9FAFB',
        padding: '18px 18px 14px',
        display: 'flex', alignItems: 'flex-start', gap: '12px',
      }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '8px', flexShrink: 0,
          background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: '18px', color: '#fff',
        }}>
          {(d.company_name || '?')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '14px', fontWeight: 700,
            color: isActive ? '#fff' : C.textMain,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {d.company_name}
          </div>
          <div style={{
            fontSize: '11px', fontFamily: 'monospace', fontWeight: 600, marginTop: '2px',
            color: isActive ? 'rgba(255,255,255,0.45)' : '#6D28D9',
          }}>
            {d.dealer_id}
          </div>
          <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{
              background: isActive ? 'rgba(52,211,153,0.18)' : 'rgba(248,113,113,0.18)',
              color:      isActive ? '#6EE7B7' : '#FCA5A5',
              border:     `1px solid ${isActive ? 'rgba(52,211,153,0.35)' : 'rgba(248,113,113,0.35)'}`,
              borderRadius: '20px', padding: '2px 10px', fontSize: '10px', fontWeight: 700,
            }}>
              {isActive ? '● Active' : '○ Inactive'}
            </span>
            {d.panel_access && (
              <span style={{
                background: 'rgba(167,139,250,0.18)',
                color: isActive ? '#C4B5FD' : '#6D28D9',
                border: '1px solid rgba(167,139,250,0.35)',
                borderRadius: '20px', padding: '2px 10px', fontSize: '10px', fontWeight: 700,
              }}>
                🔐 {d.username}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Detail rows */}
      <div style={{ padding: '14px 18px', flex: 1 }}>
        {[
          { icon: '🏢', label: 'Business', val: d.business_name },
          { icon: '👤', label: 'Contact', val: d.contact_person },
          { icon: '📞', label: 'Phone',   val: d.contact_number },
          { icon: '✉',  label: 'Email',   val: d.email          },
          { icon: '🏛', label: 'GST',     val: d.gst_number     },
        ].filter(x => x.val).map(({ icon, label, val }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '5px' }}>
            <span style={{ fontSize: '11px', flexShrink: 0, width: '16px' }}>{icon}</span>
            <span style={{ color: C.textSub, fontSize: '10px', fontWeight: 600, minWidth: '54px', flexShrink: 0 }}>{label}:</span>
            <span style={{ fontSize: '12px', color: C.textMain, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
          </div>
        ))}
      </div>

      {/* Action row */}
      <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
        <button onClick={e => { e.stopPropagation(); onToggleStatus(); }} style={{
          ...cardBtn,
          background: isActive ? C.successBg : C.dangerBg,
          color:      isActive ? C.successText : C.dangerText,
          border:     `1px solid ${isActive ? '#6EE7B7' : '#FECACA'}`,
        }}>
          {isActive ? '● Active' : '○ Inactive'}
        </button>
        <button onClick={e => { e.stopPropagation(); onEdit(); }}
          style={{ ...cardBtn, color: '#3B82F6', background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
          Edit
        </button>
        <button onClick={e => { e.stopPropagation(); onDelete(); }}
          style={{ ...cardBtn, color: C.danger, background: C.dangerBg, border: '1px solid #FECACA' }}>
          Delete
        </button>
      </div>
    </div>
  );
}

function SmBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
      background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer',
    }}>{label}</button>
  );
}

function FF({ label, error, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>
        {label}
      </label>
      {children}
      {error && <div style={{ fontSize: '11px', color: C.danger, marginTop: '4px' }}>⚠ {error}</div>}
    </div>
  );
}

function FInput({ type = 'text', value, onChange, placeholder, err = false }) {
  const [focus, setFocus] = useState(false);
  return (
    <input type={type} value={value ?? ''} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{
        width: '100%', padding: '9px 12px', boxSizing: 'border-box',
        border: `1.5px solid ${err ? C.danger : focus ? C.primary : C.border}`,
        borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none',
      }}
    />
  );
}

function FSelect({ value, onChange, children }) {
  const [focus, setFocus] = useState(false);
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{
        width: '100%', padding: '9px 12px', boxSizing: 'border-box',
        border: `1.5px solid ${focus ? C.primary : C.border}`,
        borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none',
        background: C.white, cursor: 'pointer',
      }}>
      {children}
    </select>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}>{children}</div>
  );
}

function MHeader({ title, onClose }) {
  return (
    <div style={{
      padding: '22px 28px', borderBottom: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: '#7C3AED', borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

function MFooter({ onClose, onSave, saving, label, danger = false }) {
  return (
    <div style={{
      padding: '16px 28px', borderTop: `1px solid ${C.border}`,
      display: 'flex', justifyContent: 'flex-end', gap: '10px',
    }}>
      <button onClick={onClose} style={btnGhost}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={danger ? btnDanger : btnPrimary}>
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const card     = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox = { background: C.white, borderRadius: '16px', width: '100%', maxWidth: '600px', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th       = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td       = { padding: '13px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selStyle = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = {
  padding: '10px 20px', background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
  color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700,
  cursor: 'pointer', boxShadow: '0 3px 10px rgba(124,58,237,0.3)',
};
const btnGhost  = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
const btnDanger = { padding: '10px 18px', background: C.danger, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
const cardBtn   = { padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' };
